/**
 * domains/divergence/computeDivergence.ts
 *
 * Phase 4 — divergence transparency layer (Phase 1: frontend-only)
 *
 * Compares an AI thesis (ValidatedAIResponse) against the deterministic
 * primary setup (TradeSetup) and produces a DivergenceResult describing:
 *   - Direction conflict (loudest case)
 *   - Per-field level divergence (entry, invalidation, target1)
 *   - Whether AI provided a stated reason in `uncertainty`
 *
 * This is a pure function with no React/DOM dependencies, so Phase 2 can
 * lift it into the backend AI orchestrator response handler without
 * restructuring.
 *
 * Treat the Setup Engine as the deterministic baseline. The AI Thesis is
 * an interpretive overlay. When they disagree, surface it transparently
 * rather than forcing alignment.
 *
 * v1 SCOPE NOTES:
 *   - T2 comparison is deferred. The AI's `targetLogic` is a single prose
 *     field that may reference both T1 and T2 ambiguously; comparing T2
 *     against the first parsed number would produce a weak signal. When
 *     the AI response schema gains structured targets, T2 comparison can
 *     be added back here without changing the DivergenceResult shape
 *     (target2 is already on the fields type).
 *   - `overrideReasonPresent` is true whenever `uncertainty` is non-empty.
 *     We don't apply heuristics to judge the QUALITY of the reason; the
 *     user sees the raw text and judges for themselves. Generic-text
 *     filtering may be added later if we have data showing it's needed.
 */

import type { ValidatedAIResponse } from '../ai/types/responseSchema';
import type { TradeSetup }          from '../../shared/types';
import { isNoValidSetupTradeSetup } from '../market/engines/setup2/adapter';

// --- Public types ------------------------------------------------------------

export type DivergenceLevel = 'none' | 'levels' | 'direction' | 'both';

export interface DivergenceField {
  aiText:        string;
  aiNumeric:     number | null;   // parsed from aiText (null if parse failed)
  engineNumeric: number | null;   // from setup's structured fields
  diffPct:       number | null;   // |ai - engine| / engine * 100, null if either side null
  diverges:      boolean;         // true if diffPct > LEVEL_TOLERANCE_PCT
}

export interface DivergenceResult {
  level: DivergenceLevel;

  // Direction layer
  directionConflict: boolean;
  aiBias:    ValidatedAIResponse['overallBias'];
  setupDir:  TradeSetup['dir'] | null;

  // Per-field comparisons (only populated for fields that exist on both sides)
  // target2 is intentionally omitted in v1 (see file header).
  fields: {
    entry?:        DivergenceField;
    invalidation?: DivergenceField;
    target1?:      DivergenceField;
    target2?:      DivergenceField;  // reserved for Phase 2 / structured targets
  };

  // Phase 4 -- ChatGPT review followup: capture primary setup confidence so
  // future learning attribution can distinguish "AI overrode 92% setup" from
  // "AI overrode 48% setup". Pure derived data; null when no setup exists.
  primarySetupConfidence: number | null;

  // Phase 4 -- NVS handling: true when engine evaluated the symbol and
  // explicitly found NO_VALID_SETUP. This is a first-class state, not an
  // absence of data. Distinct from "no setup at all" (e.g. !ai || !setup).
  // When true, level/direction comparisons are not meaningful; UI should
  // surface "engine sees no valid setup" instead of attempting comparisons.
  noValidSetup: boolean;

  // Transparency: did AI provide a stated reason?
  overrideReasonPresent: boolean;
  overrideReasonText:    string | null;
}

// --- Constants ---------------------------------------------------------------

/** % difference threshold for level divergence. 0.5% per Phase 4 design. */
const LEVEL_TOLERANCE_PCT = 0.5;

// --- Number extraction -------------------------------------------------------

/**
 * Extract the first plausible price-level number from an AI text field.
 * Returns null if no number is found.
 *
 * Examples of input strings the AI produces:
 *   "Wait for break below 639.00"           -> 639.00
 *   "Short on rejection at FVG 342.77-343.23" -> 342.77 (first match)
 *   "Close above 350.01"                    -> 350.01
 *   "First target 633.29 support"           -> 633.29
 *
 * Caveats:
 *   - Picks the FIRST number. If AI provides a range, we get the lower bound.
 *   - Filters out numbers that are clearly not prices (e.g. percentages, RVOL multipliers).
 */
export function extractFirstNumber(text: string | null | undefined): number | null {
  if (!text) return null;

  // Match a decimal number. Excludes %, x, X (RVOL multipliers), letters.
  const matches = text.matchAll(/(?<![a-zA-Z%])(\d+(?:\.\d+)?)(?![a-zA-Z%])/g);

  for (const m of matches) {
    const n = parseFloat(m[1]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

// --- Direction comparison ----------------------------------------------------

/**
 * Determine if the AI's directional bias contradicts the setup's direction.
 *
 * Mapping:
 *   bullish + long  -> match
 *   bearish + short -> match
 *   bullish + short -> CONTRADICTS
 *   bearish + long  -> CONTRADICTS
 *   neutral / mixed -> not a contradiction (AI is honest about uncertainty)
 */
function isDirectionConflict(
  aiBias: ValidatedAIResponse['overallBias'],
  setupDir: TradeSetup['dir'] | null,
): boolean {
  if (!setupDir) return false;
  if (aiBias === 'neutral' || aiBias === 'mixed') return false;

  if (aiBias === 'bullish' && setupDir === 'short') return true;
  if (aiBias === 'bearish' && setupDir === 'long')  return true;
  return false;
}

// --- Per-field comparison ----------------------------------------------------

/**
 * Build a DivergenceField for a single level (entry/invalidation/target1).
 * Returns undefined if the engine has no numeric value for this field
 * (we can't compare against nothing).
 */
function buildField(
  aiText:        string | null | undefined,
  engineNumeric: number | null | undefined,
): DivergenceField | undefined {
  if (engineNumeric == null || !Number.isFinite(engineNumeric)) return undefined;

  const aiNumeric = extractFirstNumber(aiText);
  const aiTextSafe = aiText ?? '';

  let diffPct: number | null = null;
  let diverges = false;

  if (aiNumeric != null && engineNumeric !== 0) {
    diffPct = Math.abs(aiNumeric - engineNumeric) / engineNumeric * 100;
    diverges = diffPct > LEVEL_TOLERANCE_PCT;
  }

  return {
    aiText:    aiTextSafe,
    aiNumeric,
    engineNumeric,
    diffPct,
    diverges,
  };
}

// --- Main entry point --------------------------------------------------------

/**
 * Compute divergence between an AI thesis and the primary setup.
 *
 * Inputs may be null (no thesis yet, no setup yet). When null, returns a
 * "no divergence" result with reasonable defaults.
 */
export function computeDivergence(
  ai:    ValidatedAIResponse | null,
  setup: TradeSetup | null,
): DivergenceResult {
  // No data -> no divergence to report
  if (!ai || !setup) {
    return {
      level:                  'none',
      directionConflict:      false,
      aiBias:                 ai?.overallBias ?? 'neutral',
      setupDir:               setup?.dir ?? null,
      fields:                 {},
      primarySetupConfidence: setup?.confidence ?? null,
      noValidSetup:           false,
      overrideReasonPresent:  false,
      overrideReasonText:     null,
    };
  }

  // Phase 4 -- NVS handling: when the engine returns a NO_VALID_SETUP sentinel,
  // it has explicitly evaluated the symbol and found no tradable setup. Level
  // and direction comparisons are not meaningful in this case (engine confidence
  // is 0, prices are null, dir is 'either'). We surface this as its own state
  // so the UI can render "engine sees no valid setup" instead of attempting
  // comparisons against the sentinel. This pattern matches how the rest of the
  // codebase handles NVS (attributionRuntime, operatorGuidanceEngine, evidence,
  // rankingEngine all treat NVS as a first-class state, not missing data).
  if (isNoValidSetupTradeSetup(setup)) {
    const trimmedUncertainty = ai.uncertainty?.trim() ?? '';
    return {
      level:                  'none',
      directionConflict:      false,
      aiBias:                 ai.overallBias,
      setupDir:               null,            // NVS has no real direction
      fields:                 {},
      primarySetupConfidence: null,            // NVS confidence is 0 by definition; null is more honest
      noValidSetup:           true,
      overrideReasonPresent:  trimmedUncertainty.length > 0,
      overrideReasonText:     trimmedUncertainty || null,
    };
  }

  // Direction layer
  const directionConflict = isDirectionConflict(ai.overallBias, setup.dir);

  // Level layer (T1 only in v1; T2 deferred -- see file header)
  const fields: DivergenceResult['fields'] = {};
  const entryF        = buildField(ai.entryLogic,    setup.entryPrice);
  const invalidationF = buildField(ai.invalidation,  setup.stopPrice);
  const target1F      = buildField(ai.targetLogic,   setup.target1Price);

  if (entryF)        fields.entry        = entryF;
  if (invalidationF) fields.invalidation = invalidationF;
  if (target1F)      fields.target1      = target1F;

  const anyLevelDiverges = Object.values(fields).some(f => f?.diverges);

  // Severity level
  let level: DivergenceLevel = 'none';
  if (directionConflict && anyLevelDiverges) level = 'both';
  else if (directionConflict)                level = 'direction';
  else if (anyLevelDiverges)                 level = 'levels';

  // Override reason -- present whenever uncertainty has any non-empty content.
  // No quality heuristic in v1; user judges the text themselves.
  const trimmedUncertainty = (ai.uncertainty ?? '').trim();
  const overrideReasonPresent = trimmedUncertainty.length > 0;
  const overrideReasonText = trimmedUncertainty || null;

  return {
    level,
    directionConflict,
    aiBias:                 ai.overallBias,
    setupDir:               setup.dir,
    fields,
    primarySetupConfidence: setup.confidence ?? null,
    noValidSetup:           false,
    overrideReasonPresent,
    overrideReasonText,
  };
}

// --- Phase 2 NOTE ------------------------------------------------------------
//
// The DivergenceResult shape was designed to map cleanly onto a snake_case
// snapshot schema for backend persistence:
//
//   ai_engine_divergence         <- result.level !== 'none'
//   divergence_type              <- result.level
//   divergence_fields            <- Object.keys(result.fields).filter(k => fields[k].diverges)
//   primary_setup_confidence     <- result.primarySetupConfidence
//   engine_no_valid_setup        <- result.noValidSetup
//   ai_override_reason           <- result.overrideReasonText
//   ai_override_reason_present   <- result.overrideReasonPresent
//
// When Phase 2 ships (backend annotation in /api/learning/snapshot), this
// function moves to the server with no shape changes -- only the import paths
// update (TypeScript types share between frontend and server).
//
// Phase 2 should add at persistence time (NOT in this pure function):
//
//   divergence_detected_at <- snapshot.timestamp (or ai.generatedAt if available)
//
// Keeping detectedAt out of the pure function preserves the property that
// (same AI + same setup) -> (same divergence result), which matters for
// idempotency and testability.
//
// --- Severity scoring (deferred) ---
//
// All raw inputs needed to compute a numeric severity score are present in
// the result shape: directionConflict, per-field 'diverges' booleans,
// overrideReasonPresent, primarySetupConfidence. Phase 2's learning pipeline
// should compute severity at persistence time using empirically tuned
// weights (NOT intuited weights). Premature weight tuning is tech debt.
//
// --- Phase 2 Backlog: Structured AI target fields ---
//
// Add structured AI target fields:
//   targets?: {
//     t1?: number | null;
//     t2?: number | null;
//   }
//
// Purpose: Remove fragile prose parsing from targetLogic, enable reliable
// T1/T2 divergence comparison, and improve snapshot/evaluator data quality.
// This is independent from overrideAcknowledged / overrideReason.
//
// PRIORITY: This should ship BEFORE overrideAcknowledged (below). Structured
// targets improve hard data quality immediately; overrideAcknowledged is
// interpretive nice-to-have.
//
// --- Phase 2 Backlog: Structured AI divergence-reason fields ---
//
// Consider adding structured AI divergence-reason fields:
//   overrideAcknowledged?: boolean
//   overrideReason?: string | null
//
// Purpose: Not to detect divergence. Divergence is detected deterministically.
// Purpose is to give the AI a structured place to explain why it intentionally
// departed from the primary setup.
//
// Only implement after backend divergence annotation and snapshot persistence
// are in place.
//
// --- Phase 2 Backlog: v1/setup2 plumbing gap for NVS visibility ---
//
// Discovered May 9 during NVS smoke testing on dev:
//
// ThesisPanel's bestSetup comes from symCtx.setups[0], which is populated by
// buildSymbolContext.ts via the legacy v1 setup engine (generateSetups from
// engines/setup/setupEngine.ts). The v1 engine returns an empty array when no
// setups are found -- it does NOT produce an NVS sentinel TradeSetup like
// setup2's adapter does.
//
// Result: When the engine has no valid setup, bestSetup is null, our
// !ai || !setup early-return fires, and noValidSetup stays false. The badge
// never appears for true NVS cases originating from setup2.
//
// Meanwhile, the Setups tab reads setup2 directly and correctly shows
// "No Setups" / "Stand aside" when setup2 returns NO_VALID_SETUP.
//
// The NVS handling code in this file (isNoValidSetupTradeSetup branch) is
// correct -- it activates once a setup2 NVS sentinel reaches the divergence
// layer. The fix is upstream: migrate symCtx.setups to read from setup2,
// or add a parallel setup2_nvs_detected boolean to symCtx that ThesisPanel
// can pass through.
//
// Until that migration: divergence layer continues to work correctly for
// cases where v1 produces setups (the common case in production -- most
// liquid symbols have v1 setups). NVS cases originating from setup2 are
// silently skipped by the divergence layer, which is a missed observability
// opportunity but not a regression -- previously, the layer rendered nothing
// useful in NVS cases anyway.

